import {
  Button,
  Icon,
  Table,
  TablePagination,
  addToast,
} from '@octano/global-ui';
import { CellFormatOptions } from '@octano/global-ui/dist/components/Table/types/TableTypes';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import DisplayError from '../../../../components/info/DisplayError';
import DisplayInfo from '../../../../components/info/DisplayInfo';
import Loading from '../../../../components/info/Loading';
import { PathsLayouts } from '../../../../config/routes';
import { useLoadingState } from '../../../../hooks/useLoadingState';
import { TableColumns } from '../../../../types/periodTuition';

import {
  Partnership as PartnershipType,
  EbTypeItem,
  getPartnershipsRequest,
  togglePartnershipActiveRequest,
  getBenefitTypesRequest,
} from '../api/partnership';
import { DisablePartnershipModal } from './parts/DisablePartnershipModal';

import styles from '../style.module.scss';

function Partnership() {
  const [partnerships, setPartnerships] = useState<PartnershipType[]>([]);
  const [totalItems, setTotalItems] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [benefitTypes, setBenefitTypes] = useState<EbTypeItem[]>([]);
  const [showDisableModal, setShowDisableModal] = useState<boolean>(false);
  const [itemToToggle, setItemToToggle] = useState<PartnershipType | null>(
    null,
  );
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [showPagination, setShowPagination] = useState<boolean>(false);

  const {
    loading,
    setLoading,
    errorLoading,
    setErrorLoading,
  } = useLoadingState();

  const history = useHistory();
  const { t } = useTranslation();
  const prefix = 'tariffAndTuition.partnerships';

  const handleToggleActive = async (item: PartnershipType) => {
    const { error } = await togglePartnershipActiveRequest(item.id);
    if (error) {
      addToast({
        icon: 'error',
        text: t(`${prefix}.toggleError`),
        color: 'danger',
      });
    } else {
      addToast({
        icon: 'check',
        text: item.isActive
          ? t(`${prefix}.disabledSuccess`)
          : t(`${prefix}.enabledSuccess`),
        color: 'success',
      });
      getPartnerships(10, currentPage - 1);
    }
    setShowDisableModal(false);
    setItemToToggle(null);
  };

  const typeLabels = Object.fromEntries(
    benefitTypes.map((bt) => [bt.id, bt.name]),
  );

  const columns: TableColumns[] = [
    {
      columnName: 'partnershipType',
      headerText: t(`${prefix}.partnershipType`),
      cellFormat: (options: CellFormatOptions) => (
        <span>{typeLabels[options.value] || options.value || '-'}</span>
      ),
    },
    {
      columnName: 'name',
      headerText: t(`${prefix}.partnershipName`),
      bold: true,
    },
    {
      columnName: 'description',
      headerText: t(`${prefix}.description`),
      cellFormat: (options: CellFormatOptions) => (
        <span
          className="text-muted"
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            maxWidth: '300px',
          }}
        >
          {options.value || '-'}
        </span>
      ),
    },
    {
      columnName: 'id',
      headerText: t('common.actions.action'),
      cellFormat: (options: CellFormatOptions) => (
        <Button
          text={
            options.row.isActive
              ? t(`${prefix}.disable`)
              : t(`${prefix}.enable`)
          }
          type="button"
          size="sm"
          outlined
          onClick={() => {
            setItemToToggle(options.row);
            if (options.row.isActive) {
              setShowDisableModal(true);
            } else {
              handleToggleActive(options.row);
            }
          }}
        />
      ),
    },
    {
      columnName: 'id',
      headerText: '',
      width: '60px',
      cellFormat: (options: CellFormatOptions) => (
        <span
          onClick={() => {
            history.push(
              `${PathsLayouts.tariffAndTuiton}/partnerships/edit/${options.row.id}`,
            );
          }}
          className="cursor-pointer"
          style={{ display: 'flex', justifyContent: 'center' }}
        >
          <Icon name="edit" key="edit" color="primary" />
        </span>
      ),
    },
  ];

  const getPartnerships = useCallback(
    async (items: number = 10, page: number = 0) => {
      const { data, error } = await getPartnershipsRequest(items, page);

      if (error) {
        setErrorLoading(error.code);
        return;
      }

      if (data) {
        setErrorLoading(undefined);
        const {
          data: partnershipItems = [],
          total = 0,
          total_pages = 1,
        } = data;
        setPartnerships(partnershipItems);
        setTotalItems(total);
        setTotalPages(total_pages);
        setShowPagination(true);
        setCurrentPage(page + 1);
      }

      setLoading(false);
    },
    [setErrorLoading, setLoading],
  );

  useEffect(() => {
    getPartnerships();
    getBenefitTypesRequest('simple').then(({ data }) => {
      if (data) {
        setBenefitTypes(data);
      }
    });
  }, [getPartnerships]);

  if (errorLoading) {
    return (
      <DisplayError
        insideCard
        textBody={errorLoading}
        retryAction={() => setLoading(true)}
      />
    );
  }

  if (loading) {
    return <Loading insideCard />;
  }

  // Show list view
  return (
    <div className="g-table-container">
      <Button
        text={t(`${prefix}.addNew`)}
        type="button"
        size="sm"
        icon="plus"
        onClick={() =>
          history.push(`${PathsLayouts.tariffAndTuiton}/partnerships/create`)
        }
        className="g-add-button mb-3 mt-3"
      />

      <div className={styles.list}>
        <Table
          columns={columns}
          data={partnerships}
          noResultsText={<div></div>}
        />
        {partnerships.length === 0 && (
          <DisplayInfo
            insideCard
            title={t(`${prefix}.emptyTitle`)}
            textBody={t(`${prefix}.emptyBody`)}
          />
        )}
        {showPagination && totalItems > 10 && (
          <TablePagination
            pagination={{
              currentPage,
              itemsPerPage: 10,
              totalItems,
              totalPages,
              onChangePage: (page) => getPartnerships(10, page - 1),
            }}
          />
        )}
      </div>

      <DisablePartnershipModal
        isOpen={showDisableModal}
        onClose={() => {
          setShowDisableModal(false);
          setItemToToggle(null);
        }}
        onConfirm={() => itemToToggle && handleToggleActive(itemToToggle)}
        partnershipName={itemToToggle?.name || ''}
      />
    </div>
  );
}

export default Partnership;
